export const REGEX = {
  email: /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
  leastOneLetter: /\w*[a-zA-Z]\w*/,
}

// Common TLD typos and their corrections
const TLD_TYPO_MAP: Record<string, string> = {
  '.con': '.com',
  '.cmo': '.com',
  '.cim': '.com',
  '.ocm': '.com',
  '.vom': '.com',
  '.xom': '.com',
  '.dom': '.com',
  '.comm': '.com',
  '.nte': '.net',
  '.ent': '.net',
  '.ogr': '.org',
  '.orh': '.org',
  '.eud': '.edu',
  '.esu': '.edu',
}

// Common domain typos and their corrections
const DOMAIN_TYPO_MAP: Record<string, string> = {
  'gmal.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmil.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmali.com': 'gmail.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yhaoo.com': 'yahoo.com',
  'tahoo.com': 'yahoo.com',
  'hotmal.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotamil.com': 'hotmail.com',
  'outloo.com': 'outlook.com',
  'outlok.com': 'outlook.com',
  'outllook.com': 'outlook.com',
  'iclod.com': 'icloud.com',
  'icoud.com': 'icloud.com',
  'icloud.con': 'icloud.com',
}

// Valid common TLDs (single-part only — multi-part like co.uk are covered by their components)
const VALID_TLDS = new Set([
  'com',
  'net',
  'org',
  'edu',
  'gov',
  'mil',
  'int',
  'io',
  'co',
  'us',
  'uk',
  'ca',
  'au',
  'de',
  'fr',
  'jp',
  'cn',
  'in',
  'br',
  'ru',
  'mx',
  'es',
  'it',
  'nl',
  'se',
  'no',
  'dk',
  'fi',
  'at',
  'ch',
  'be',
  'nz',
  'za',
  'ie',
  'pt',
  'pl',
  'cz',
  'hu',
  'ro',
  'bg',
  'hr',
  'sk',
  'si',
  'info',
  'biz',
  'name',
  'pro',
  'museum',
  'coop',
  'aero',
  'app',
  'dev',
  'tech',
  'online',
  'store',
  'site',
  'xyz',
  'club',
  'live',
  'me',
])

/**
 * Checks an email for common typos and returns a suggested correction.
 * Returns null if no typo is detected.
 */
export const getEmailTypoSuggestion = (email: string): string | null => {
  if (!email) return null

  const lowerEmail = email.toLowerCase().trim()
  const atIndex = lowerEmail.lastIndexOf('@')
  if (atIndex === -1) return null

  const localPart = lowerEmail.substring(0, atIndex)
  const domain = lowerEmail.substring(atIndex + 1)

  // Check for full domain typos first (e.g. gmal.com -> gmail.com)
  if (DOMAIN_TYPO_MAP[domain]) {
    return `${localPart}@${DOMAIN_TYPO_MAP[domain]}`
  }

  // Check for TLD typos (e.g. .con -> .com)
  const lastDotIndex = domain.lastIndexOf('.')
  if (lastDotIndex === -1) return null

  const tld = domain.substring(lastDotIndex)
  const domainWithoutTld = domain.substring(0, lastDotIndex)

  if (TLD_TYPO_MAP[tld]) {
    return `${localPart}@${domainWithoutTld}${TLD_TYPO_MAP[tld]}`
  }

  return null
}

/**
 * Checks if the email's TLD is in the known valid TLD list.
 */
export const isKnownTld = (email: string): boolean => {
  if (!email) return false

  const lowerEmail = email.toLowerCase().trim()
  const atIndex = lowerEmail.lastIndexOf('@')
  if (atIndex === -1) return false

  const domain = lowerEmail.substring(atIndex + 1)
  const lastDotIndex = domain.lastIndexOf('.')
  if (lastDotIndex === -1) return false

  const tld = domain.substring(lastDotIndex + 1)
  return VALID_TLDS.has(tld)
}

export const validate = {
  email: (email: string) => {
    return REGEX.email.test(String(email).toLowerCase())
  },
  leastOneLetter: (value: string) => {
    return REGEX.leastOneLetter.test(value)
  },
}
